import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { httpGetUser, httpGetMyUser, httpUpdateUser, httpDeleteUser } from 'services/users';
import { useAuthContext } from 'contexts/AuthContext';
import { USER_ROLE_LABELS, getUserRoleLabel } from './userRoleLabels';
import Modal from './Modal';
import './EditPumpModal.scss';
import './UserDetailPanel.scss';

const roleMap = USER_ROLE_LABELS;

export default function UserDetailPanel({ userId, onBack, onUserUpdated, onUserDeleted }) {
  const { t } = useTranslation();
  const { myUser: currentUser, userRole } = useAuthContext();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const isOwnProfile = user && currentUser && (user.id === currentUser.id || userId === 'me');
  const viewingOwnUserId =
    currentUser &&
    (userId === 'me' || (userId != null && String(userId) === String(currentUser.id)));
  const canManageRoles =
    userRole === 1 ||
    userRole === 2 ||
    userRole === 4 ||
    userRole === 5 ||
    (userRole === 3 && !viewingOwnUserId);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setUser(null);

    const fetchUserData = async () => {
      try {
        let response;
        if (userId === 'me') {
          response = await httpGetMyUser();
        } else {
          response = await httpGetUser(userId);
        }
        
        const userData = response.data;
        setUser(userData);
        const co = userData?.company;
        const companyName = userData?.company_name || '';
        const companyFieldLocked =
          userRole === 2 || userRole === 3 || userRole === 4 || userRole === 5;
        // API returns company as PK (number); company_name is the display label.
        const companyForForm = companyFieldLocked
          ? companyName || (co != null && co !== '' ? String(co) : '')
          : typeof co === 'object' && co != null
            ? String(co.id ?? '')
            : co != null && co !== ''
              ? String(co)
              : companyName;
        setFormData({
          first_name: userData?.first_name || '',
          last_name: userData?.last_name || '',
          email: userData?.email || '',
          phone: userData?.phone || userData?.phone_number || '',
          company: companyForForm,
          role: userData?.role || userData?.groups?.[0] || 4
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Virhe käyttäjätietojen haussa');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId, currentUser, userRole]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
      };

      if (userRole !== 2 && userRole !== 3 && userRole !== 4 && userRole !== 5) {
        const c = formData.company;
        if (c !== '' && c != null) {
          const n = parseInt(c, 10);
          updateData.company = Number.isNaN(n) ? c : n;
        }
      }

      if (canManageRoles && !isOwnProfile) {
        updateData.role = parseInt(formData.role, 10);
      }

      const response = await httpUpdateUser(user.id, updateData);
      
      if (response?.status === 200) {
        setSuccess('Käyttäjätiedot päivitetty onnistuneesti');
        const updatedUser = { ...user, ...response.data };
        setUser(updatedUser);
        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }
      } else {
        setError('Päivitys epäonnistui');
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.message || 'Virhe käyttäjätietojen päivityksessä');
    } finally {
      setSaving(false);
    }
  };

  const getUserFullName = (u) => {
    const firstName = u?.first_name || '';
    const lastName = u?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || u?.email || 'Käyttäjä';
  };

  const canDeleteMaintenanceUser =
    userRole === 5 && !isOwnProfile && user && Number(user.role) === 5;

  const canDeleteMineUser =
    userRole === 4 &&
    !isOwnProfile &&
    user &&
    [4, 5].includes(Number(user.role));

  const canDeleteRepairerUser =
    userRole === 3 && !isOwnProfile && user && Number(user.role) === 3;

  const openDeleteConfirm = () => {
    if (
      (!canDeleteMaintenanceUser && !canDeleteMineUser && !canDeleteRepairerUser) ||
      !user?.id
    ) {
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setDeleteConfirmOpen(false);
  };

  const executeDeleteUser = async () => {
    if (
      (!canDeleteMaintenanceUser && !canDeleteMineUser && !canDeleteRepairerUser) ||
      !user?.id
    ) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await httpDeleteUser(user.id);
      setDeleteConfirmOpen(false);
      if (onUserDeleted) {
        onUserDeleted(user.id);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.detail || 'Käyttäjän poisto epäonnistui');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="user-detail-panel">
        <div className="details-header">
          {onBack && (
            <button className="back-btn" onClick={onBack}>←</button>
          )}
          <h2 className="details-title">Käyttäjän tiedot</h2>
          <div style={{ width: '48px' }}></div>
        </div>
        <div className="user-details-content">
          <div className="text-center py-5">Ladataan...</div>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="user-detail-panel">
        <div className="details-header">
          {onBack && (
            <button className="back-btn" onClick={onBack}>←</button>
          )}
          <h2 className="details-title">Käyttäjän tiedot</h2>
          <div style={{ width: '48px' }}></div>
        </div>
        <div className="user-details-content">
          <div style={{ 
            color: '#b71c1c', 
            backgroundColor: '#fde0e0', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '16px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-detail-panel">
      <Modal
        isOpen={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        title={t('user_delete_confirm_title')}
        size="small"
      >
        <p className="user-delete-confirm-text" style={{ margin: 0, lineHeight: 1.5, color: '#333' }}>
          {t('user_delete_confirm_body', { name: getUserFullName(user) })}
        </p>
        <div className="form-actions" style={{ marginTop: '20px' }}>
          <button type="button" className="btn-secondary" onClick={closeDeleteConfirm} disabled={deleting}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn-primary"
            style={{ backgroundColor: '#b71c1c', borderColor: '#b71c1c' }}
            onClick={executeDeleteUser}
            disabled={deleting}
          >
            {deleting ? t('user_delete_in_progress') : t('user_delete_confirm_btn')}
          </button>
        </div>
      </Modal>

      <div className="details-header">
        {onBack && (
          <button className="back-btn" onClick={onBack}>←</button>
        )}
        <h2 className="details-title">
          {isOwnProfile ? 'Oma tili' : getUserFullName(user)}
        </h2>
        <div style={{ width: '48px' }}></div>
      </div>
      <div className="user-details-content">
        <form className="user-form" onSubmit={handleSubmit}>
          {error && (
            <div style={{ 
              color: '#b71c1c', 
              backgroundColor: '#fde0e0', 
              padding: '12px', 
              borderRadius: '8px', 
              marginBottom: '16px',
              fontSize: '0.9rem'
            }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ 
              color: '#2e7d32', 
              backgroundColor: '#e8f5e8', 
              padding: '12px', 
              borderRadius: '8px', 
              marginBottom: '16px',
              fontSize: '0.9rem'
            }}>
              {success}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Etunimi</label>
            <input 
              className="form-input" 
              type="text" 
              value={formData.first_name || ''} 
              onChange={(e) => handleInputChange('first_name', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Sukunimi</label>
            <input 
              className="form-input" 
              type="text" 
              value={formData.last_name || ''} 
              onChange={(e) => handleInputChange('last_name', e.target.value)}
            />
          </div>

          <div className="form-group full-width">
            <label className="form-label">Käyttäjätunnus (sähköposti)</label>
            <input 
              className="form-input" 
              type="email" 
              value={formData.email || ''} 
              disabled
              style={{ 
                backgroundColor: '#f5f5f5', 
                color: '#666',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Puhelin</label>
            <input 
              className="form-input" 
              type="text" 
              value={formData.phone || ''} 
              onChange={(e) => handleInputChange('phone', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Käyttäjäoikeus</label>
            {canManageRoles && !isOwnProfile ? (
              <select
                className="form-select"
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
              >
                {Object.entries(roleMap)
                  .filter(([id]) => {
                    if (userRole === 2) return id !== '1';
                    if (userRole === 5) return id === '5';
                    if (userRole === 4) return ['4', '5'].includes(id);
                    if (userRole === 3) return id === '3';
                    return true;
                  })
                  .map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            ) : (
              <input 
                className="form-input" 
                type="text" 
                value={getUserRoleLabel(formData.role)} 
                disabled
                style={{ 
                  backgroundColor: '#f5f5f5', 
                  color: '#666',
                  cursor: 'not-allowed'
                }}
              />
            )}
            {!canManageRoles && (
              <div className="helper-text">
                Vain pääkäyttäjä, YTM, toisen korjaajan profiilissa korjaaja, kaivoskäyttäjä tai huolto voi
                vaihtaa roolin
              </div>
            )}
          </div>

          <div className="form-group full-width">
            <label className="form-label">
              {userRole === 2 || userRole === 3 || userRole === 4 || userRole === 5
                ? 'Yritys'
                : 'Yritys (ID)'}
            </label>
            <input 
              className="form-input" 
              type="text" 
              value={formData.company || ''} 
              onChange={(e) => handleInputChange('company', e.target.value)}
              disabled={userRole === 2 || userRole === 3 || userRole === 4 || userRole === 5}
              style={
                userRole === 2 || userRole === 3 || userRole === 4 || userRole === 5
                  ? { backgroundColor: '#f5f5f5', color: '#666', cursor: 'not-allowed' }
                  : undefined
              }
            />
            {(userRole === 2 || userRole === 3 || userRole === 4 || userRole === 5) && (
              <div className="helper-text">
                {userRole === 2
                  ? 'Yritystä ei voi muuttaa YTM-käyttäjällä'
                  : userRole === 3
                    ? 'Yritystä ei voi muuttaa korjaajalla'
                    : userRole === 4
                      ? 'Yritystä ei voi muuttaa kaivoskäyttäjällä'
                      : 'Yritystä ei voi muuttaa huoltokäyttäjällä'}
              </div>
            )}
            {userRole === 1 && user?.company_name && (
              <div className="helper-text">{user.company_name}</div>
            )}
          </div>

          <button 
            className="save-btn" 
            type="submit" 
            disabled={saving}
          >
            {saving ? 'Tallennetaan...' : 'Tallenna'}
          </button>

          {(canDeleteMaintenanceUser || canDeleteMineUser || canDeleteRepairerUser) && (
            <button
              type="button"
              className="save-btn"
              style={{
                marginTop: '12px',
                backgroundColor: '#b71c1c',
                borderColor: '#b71c1c',
              }}
              disabled={deleting}
              onClick={openDeleteConfirm}
            >
              {deleting
                ? 'Poistetaan...'
                : canDeleteMineUser
                  ? Number(user?.role) === 5
                    ? 'Poista huoltokäyttäjä'
                    : 'Poista kaivoskäyttäjä'
                  : canDeleteRepairerUser
                    ? 'Poista korjaaja'
                    : 'Poista huoltokäyttäjä'}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

